import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/useAuth';
import { useApi } from '../lib/api';
import { cn } from '../lib/utils';
import {
    User,
    Bell,
    Save,
    Loader2,
    Check,
} from 'lucide-react';

interface Preferences {
    teamsOnApproval: boolean;
    teamsOnRejection: boolean;
    teamsWeeklyReminder: boolean;
    teamsPendingApprovals: boolean;
}

const PREFERENCES_PAR_DEFAUT: Preferences = {
    teamsOnApproval: true,
    teamsOnRejection: true,
    teamsWeeklyReminder: true,
    teamsPendingApprovals: true,
};

export function Settings() {
    const { user } = useAuth();
    const api = useApi();
    const queryClient = useQueryClient();

    // Préférences enregistrées, puis ce que la personne a changé depuis. Les garder à part
    // empêche une lecture arrivée après un clic d'écraser ce clic. La langue et l'affichage
    // des week-ends ont été retirés : aucun écran ne les appliquait.
    const { data: enregistrees } = useQuery({
        queryKey: ['user', 'settings'],
        queryFn: () => api.get<{ data: Partial<Preferences> }>('/api/users/me/settings'),
    });
    const [modifiees, setModifiees] = useState<Partial<Preferences>>({});
    const settings: Preferences = { ...PREFERENCES_PAR_DEFAUT, ...enregistrees?.data, ...modifiees };
    const setSettings = (suivantes: Preferences) => setModifiees(suivantes);

    const [saved, setSaved] = useState(false);

    const saveMutation = useMutation({
        mutationFn: (newSettings: Preferences) =>
            api.put('/api/users/me/settings', newSettings),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['user'] });
            setModifiees({});
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        },
    });

    const handleSave = () => {
        saveMutation.mutate(settings);
    };

    return (
        <div className="max-w-2xl mx-auto space-y-6 px-4 sm:px-0">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-[var(--color-text)]">Paramètres</h1>
                <p className="text-[var(--color-text-secondary)]">Personnalisez votre expérience</p>
            </div>

            {/* Profile section */}
            <div className="card overflow-hidden">
                <div className="-m-6 mb-4 p-4 border-b border-[var(--color-border)]">
                    <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-[var(--color-text-muted)]" />
                        <h2 className="font-semibold text-[var(--color-text)]">Profil</h2>
                    </div>
                </div>
                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-[var(--color-primary-light)] flex items-center justify-center">
                            <span className="text-[var(--color-primary)] text-xl font-medium">
                                {user?.displayName
                                    ?.split(' ')
                                    .map((n: string) => n[0])
                                    .join('')
                                    .toUpperCase()
                                    .slice(0, 2) || 'U'}
                            </span>
                        </div>
                        <div>
                            <p className="font-medium text-[var(--color-text)]">{user?.displayName || 'Utilisateur'}</p>
                            <p className="text-sm text-[var(--color-text-secondary)]">{user?.email}</p>
                            <p className="text-xs text-[var(--color-text-muted)] mt-1">
                                Rôle: {user?.role === 'admin' ? 'Administrateur' :
                                    user?.role === 'manager' ? 'Manager' :
                                        user?.role === 'billing' ? 'Facturation' : 'Collaborateur'}
                            </p>
                        </div>
                    </div>
                    <p className="text-sm text-[var(--color-text-secondary)] bg-[var(--color-bg)] p-3 rounded-lg">
                        Les informations de profil sont synchronisées depuis Microsoft Entra ID.
                        Contactez votre administrateur pour toute modification.
                    </p>
                </div>
            </div>

            {/* Notifications section */}
            <div className="card overflow-hidden">
                <div className="-m-6 mb-4 p-4 border-b border-[var(--color-border)]">
                    <div className="flex items-center gap-2">
                        <Bell className="w-5 h-5 text-[var(--color-text-muted)]" />
                        <h2 className="font-semibold text-[var(--color-text)]">Notifications</h2>
                    </div>
                </div>
                <div className="space-y-4">
                    <p className="text-xs text-[var(--color-text-muted)]">
                        Les notifications sont envoyées dans Microsoft Teams via le bot Clippit CRA.
                    </p>
                    <ToggleSetting
                        label="Saisie approuvée"
                        description="Quand l'une de vos saisies est validée"
                        checked={settings.teamsOnApproval}
                        onChange={(v) => setSettings({ ...settings, teamsOnApproval: v })}
                    />
                    <ToggleSetting
                        label="Saisie rejetée"
                        description="Quand l'une de vos saisies est rejetée"
                        checked={settings.teamsOnRejection}
                        onChange={(v) => setSettings({ ...settings, teamsOnRejection: v })}
                    />
                    <ToggleSetting
                        label="Rappel hebdomadaire"
                        description="Chaque vendredi, si des saisies sont manquantes"
                        checked={settings.teamsWeeklyReminder}
                        onChange={(v) => setSettings({ ...settings, teamsWeeklyReminder: v })}
                    />
                    <ToggleSetting
                        label="Approbations en attente"
                        description="Saisies à valider (managers et admins)"
                        checked={settings.teamsPendingApprovals}
                        onChange={(v) => setSettings({ ...settings, teamsPendingApprovals: v })}
                    />
                </div>
            </div>

            {/* Save button */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-4">
                {saved && (
                    <span className="flex items-center justify-center gap-2 text-[var(--color-success)]">
                        <Check className="w-4 h-4" />
                        Paramètres enregistrés
                    </span>
                )}
                <button
                    onClick={handleSave}
                    disabled={saveMutation.isPending}
                    className="btn-primary justify-center"
                >
                    {saveMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Save className="w-4 h-4" />
                    )}
                    Enregistrer
                </button>
            </div>
        </div>
    );
}

interface ToggleSettingProps {
    label: string;
    description?: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
}

function ToggleSetting({ label, description, checked, onChange }: ToggleSettingProps) {
    return (
        <div className="flex items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
                <p className="font-medium text-[var(--color-text)]">{label}</p>
                {description && <p className="text-sm text-[var(--color-text-secondary)]">{description}</p>}
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={label}
                onClick={() => onChange(!checked)}
                className={cn(
                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2',
                    checked ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-border)]'
                )}
            >
                <span
                    className={cn(
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                        checked ? 'translate-x-5' : 'translate-x-0'
                    )}
                />
            </button>
        </div>
    );
}


